import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faChartLine,
  faHome,
  faRightFromBracket,
  faSort,
  faTable,
  faTableList,
  faUser,
  faUserGroup,
  IconDefinition,
  faListCheck,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import router from "../../components/routing/router";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { environment } from "../../environments/environment";
import "../../stylesheet/sidebar.css";
import qnetLogo from "../../assets/qnet-logo-no-bg.png";
import { Supplier } from "../../models/supplier";
import { useNavigate } from "react-router-dom";
import { generateToken } from "../../reducers/sessionSlice";
import { useAppDispatch, useAppSelector } from "../../reducers/store";
import { 
  getSupplierData, 
  setSelectedSupplier, 
  setSelectedProducingFacilities, 
  setInitialLoadComplete, 
  getAgreementsBySupplier,
  selectSelectedSupplier,
  selectSelectedProducingFacilities,
  selectSelectedProducingFacilitiesFiltered,
  selectInitialLoadComplete,
  selectAllSuppliers,
  selectParentSuppliers,
  selectProducingFacilities,
  selectAgreements,
  setProducingFacilities,
  selectSupplierWithAgreements
} from "../../reducers/supplierSlice";
import { processAgreements } from "../../reducers/locationSlice";

interface SidebarOptions {
  name: string;
  path?: string;
  icon: IconDefinition;
  selected: boolean;
  rightIcon?: IconDefinition;
  ref?: string;
}

interface GroupedSidebarOptions {
  name: string | null;
  options: SidebarOptions[];
}

export function Sidebar() {
  const dispatch = useAppDispatch();
  const selectedSupplier = useAppSelector(selectSelectedSupplier);
  const selectedProducingFacilities = useAppSelector(selectSelectedProducingFacilities);
  const selectedProducingFacilitiesFiltered = useAppSelector(selectSelectedProducingFacilitiesFiltered);
  const initialLoadComplete = useAppSelector(selectInitialLoadComplete);
  const allSuppliers = useAppSelector(selectAllSuppliers);
  const parentSuppliers = useAppSelector(selectParentSuppliers);
  const producingFacilities = useAppSelector(selectProducingFacilities);
  const agreements = useAppSelector(selectAgreements);

  const [sideBarOptions, setSideBarOptions] = useState<GroupedSidebarOptions[]>(
    [
      {
        name: null,
        options: [
          {
            name: "Home",
            path: "/home",
            icon: faHome,
            selected: false,
          },
          {
            name: "Approvals",
            path: "/approvals",
            icon: faListCheck,
            selected: false,
          },
          {
            name: "Teams & Contacts",
            path: "/team",
            icon: faUserGroup,
            selected: false,
          },
        ],
      },
      {
        name: "ANALYTICS",
        options: [
          {
            name: "Analytics",
            path: "/analytics",
            icon: faChartLine,
            selected: false,
          },
          {
            name: "Reports",
            icon: faTableList,
            rightIcon: faArrowUpRightFromSquare,
            selected: false,
            ref: environment.customerDashboardUrl,
          },
        ],
      },
      {
        name: "OPERATIONS",
        options: [
          {
            name: "Account Settings",
            path: "/settings",
            icon: faUser,
            selected: false,
          },
        ],
      },
    ],
  );

  const supplierIconUrl =
    "https://s3.amazonaws.com/com.teamqualityservices.qnet/images/Logo+Q+Only.png";

  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [processingReport, setProcessingReport] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    processLocationChange();
  }, [location]);

  useEffect(() => {
    const getSuppliers = async () => {
      await dispatch(getSupplierData());
    };

    setTimeout(() => {
      getSuppliers();
    }, 0);
  }, []);

  const setChildData = async (supplier: Supplier) => {
    // Get agreements and process them
    await dispatch(getAgreementsBySupplier(supplier.id));
    dispatch(processAgreements());

    setTimeout(() => {
      dispatch(setInitialLoadComplete(true));
    }, 0);
  };

  async function handleViewReport() {
    if (processingReport || !selectedSupplier?.id) {
      return;
    }

    setProcessingReport(true);
    const customerPortalUrl = environment.customerDashboardUrl;

    try {
      const result = await dispatch(generateToken(selectedSupplier.id));
      
      if (result.payload) {
        const secretKey = result.payload.token;
        const embeddedLink = `${customerPortalUrl}/#${encodeURIComponent(secretKey)}`;
        window.open(embeddedLink, "_blank");
      }
    } catch (error) {
      console.error("Error generating token or opening report:", error);
    }

    setProcessingReport(false);
  }

  const selectSupplier = (supplier: Supplier) => {
    dispatch(selectSupplierWithAgreements(supplier));
    localStorage.setItem("selected_supplier", JSON.stringify(supplier));
    setChildData(supplier);
  };

  const processLocationChange = () => {
    const optionsCopy = JSON.parse(JSON.stringify(sideBarOptions));

    optionsCopy.forEach((group: GroupedSidebarOptions) => {
      group.options.forEach((option: SidebarOptions) => {
        if (option.path === location.pathname) {
          option.selected = true;
        } else {
          option.selected = false;
        }
      });
    });

    setSideBarOptions(optionsCopy);
  };

  const updateSelectedProductingFacilities = (
    e: React.ChangeEvent<HTMLInputElement>,
    supplier: Supplier,
  ) => {
    var newArray = [];
    if (e.target.checked) {
      newArray = [...selectedProducingFacilities, supplier];
    } else {
      newArray = selectedProducingFacilities.filter(
        (s) => s.id !== supplier.id,
      );
    }

    dispatch(setSelectedProducingFacilities(newArray));
    localStorage.setItem(
      "selected_producting_facilities",
      JSON.stringify(newArray),
    );
  };

  const toggleSidebar = (value: boolean = !open) => {
    setOpen(value);

    if (!value) {
      setSupplierOpen(false);
    }
  };

  const logOut = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("selected_supplier");
    localStorage.removeItem("selected_producting_facilities");

    router.navigate("/login");
  };

  const processNav = (option: SidebarOptions) => {
    if (option.name === "Reports") {
      handleViewReport();
    } else if (option.path) {
      navigate(option.path);
    }
  };

  return (
    <div
      className="z-[1] h-screen w-[max-content] border-[1px] bg-white p-[8px] fixed"
      onMouseEnter={() => toggleSidebar(true)}
      onMouseLeave={() => toggleSidebar(false)}
    >
      <div
        className={`flex h-[100%] flex-col justify-between border-gray-200 ${open ? "open-sidebar" : "close-sidebar"}`}
      >
        <div className="flex h-[100px] items-center justify-center ">
          {!open && (
            <img className="h-[40px] w-[40px]" src={qnetLogo} alt="QNET Logo" />
          )}
          {open && (
            <div
              className="flex flex-row items-center gap-x-[6px] overflow-hidden rounded-[4px] bg-[#EDEDED] p-[8px] hover:cursor-pointer"
              onClick={() => setSupplierOpen(!supplierOpen)}
            >
              <div className="w-[32px]">
                <div className="flex h-[32px] w-[32px] items-center justify-center rounded-[50%] bg-black">
                  <img
                    className="h-[18px] w-[18px] min-w-[18px]"
                    src={supplierIconUrl}
                    alt="Supplier Icon"
                  />
                </div>
              </div>
              <div className="flex w-[calc(100%-60px)] flex-col justify-center">
                <div className="flex flex-row">
                  <span className="text-[10px] text-[#212322]">Supplier</span>
                </div>
                <div className="ellipse-text flex flex-row text-[black]">
                  <span className="ellipse-text text-[11px] font-bold text-[black]">
                    {selectedSupplier?.name ?? "No Supplier Found"}
                  </span>
                </div>
              </div>
              <div className="w-[28px] text-[12px] text-[#00000040]">
                <FontAwesomeIcon icon={faSort} />
              </div>
            </div>
          )}
          {supplierOpen && (
            <div className="z-index-[100001] absolute top-[90px] flex w-[calc(100%-16px)] flex-col gap-y-[10px]">
              <div className="flex flex-col rounded-[4px] border-[1px] border-[#EDEDED] bg-white shadow">
                <div className="border-b-[1px] border-[#EDEDED] p-[6px] text-[12px] font-semibold text-[black]">
                  <span>Company Switch</span>
                </div>
                <div className="flex flex-col">
                  {parentSuppliers.map((supplier, index) => (
                    <div
                      className="ellipse-text flex flex-row items-center justify-between gap-x-[4px] pr-[4px]"
                      key={index}
                    >
                      <span
                        className={`p-[6px] text-[12px] text-[black] ${selectedSupplier?.id === supplier.id ? "font-bold" : ""}`}
                      >
                        {supplier.name}
                      </span>
                      <input
                        checked={selectedSupplier?.id === supplier.id}
                        onClick={() => selectSupplier(supplier)}
                        onChange={() => {}}
                        type="radio"
                        name="radio-1"
                        className="radio-custom max-h-[20px] min-h-[20px] min-w-[20px] max-w-[20px]"
                        style={{
                          borderColor:
                            selectedSupplier?.id === supplier.id
                              ? "#fae752"
                              : "gray",
                          borderWidth: "2px",
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {producingFacilities.length > 0 && (
                <div className="flex flex-col rounded-[4px] border-[1px] border-[#EDEDED] bg-white shadow">
                  <div className="border-b-[1px] border-[#EDEDED] p-[6px] text-[12px] font-semibold text-[black]">
                    <span>Producing Facility Selection</span>
                  </div>
                  <div className="mb-2 ml-1 mt-2 flex flex-col space-y-1">
                    {" "}
                    {producingFacilities.map((supplier, index) => (
                      <div
                        className={`ellipse-text flex min-w-[20px] max-w-[190px] cursor-pointer flex-row items-center justify-between rounded-md p-[4px] ${selectedProducingFacilities.some((s) => s.id === supplier.id) ? "bg-gray-200" : ""}`}
                        key={index}
                        onClick={() =>
                          updateSelectedProductingFacilities(
                            {
                              target: {
                                checked: !selectedProducingFacilities.some(
                                  (s) => s.id === supplier.id,
                                ),
                              },
                            } as any,
                            supplier,
                          )
                        }
                      >
                        <span
                          className={`ml-1 min-w-[100px] text-[12px] text-[black] ${selectedProducingFacilities.some((s) => s.id === supplier.id) ? "font-bold" : ""}`}
                        >
                          {supplier.name}
                        </span>
                        <input
                          onChange={(e) =>
                            updateSelectedProductingFacilities(e, supplier)
                          }
                          checked={selectedProducingFacilities.some(
                            (s) => s.id === supplier.id,
                          )}
                          type="checkbox"
                          className="checkbox max-h-[16px] max-w-[16px] rounded-[50%] border-[#212322] [--chkbg:#fedd00] [--chkfg:black]"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex h-[calc(100vh-150px)] flex-col text-[14px] text-[#212322] ">
          {sideBarOptions.map((group, index) => (
            <div key={index}>
              <div className="flex flex-col justify-start">
                {group.name && (
                  <span
                    className={`pt-[22px] text-[14px] text-[#212322] ${open ? "" : "opacity-0"}`}
                  >
                    {group.name}
                  </span>
                )}
                <div>
                  {group.options.map((option, index) => (
                    <div
                      onClick={() => processNav(option)}
                      className={`mt-[4px] flex h-[34px] items-center p-[8px] text-[14px] ${open ? "justify-between" : "justify-center"} rounded-[4px] hover:cursor-pointer ${option.selected ? "bg-[#fedd00]" : "hover:bg-[#fedd0030]"}`}
                      key={index}
                    >
                      <button className={`flex items-center gap-x-[10px]`}>
                        <FontAwesomeIcon className={`${(processingReport && option.name === 'Reports') ? 'animate-spin' : ''}`} 
                        icon={(processingReport && option.name === 'Reports') ? faSpinner : option.icon} />
                        {open && (
                          <span className="ellipse-text">{option.name}</span>
                        )}
                      </button>
                      {option.rightIcon && open && (
                        <FontAwesomeIcon
                          className="text-[12px] text-[grey]"
                          icon={option.rightIcon}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className={`flex h-[50px]  rounded-[4px] p-[8px] hover:cursor-pointer hover:bg-[#fedd0030] ${open ? "" : "justify-center"}`}
        >
          <button
            onClick={() => logOut()}
            className="flex items-center gap-x-[10px] text-[14px] text-[#212322]"
          >
            <FontAwesomeIcon icon={faRightFromBracket} />
            {open && <span className="ellipse-text">Log out</span>}
          </button>
        </div>
      </div>
    </div>
  );
}
